/**
 * ConfidenceMasterPanel — histograma SVG dos valores de confidence.
 *
 * Bucketiza em 10 faixas (0-9, 10-19, ..., 90-100) e mostra contagem +
 * média + mediana. Permite mestre ler a distribuição em tempo real.
 */

import type { MissionComponentProps } from '../../../games/atlas/components'
import { useLabComponentState } from './useLabComponentState'

interface Response {
  membershipId?: string
  value?: number
}

const BUCKET_COUNT = 10
const BUCKET_SIZE = 10

function bucketize(values: number[]): number[] {
  const buckets = new Array<number>(BUCKET_COUNT).fill(0)
  for (const v of values) {
    let idx = Math.floor(v / BUCKET_SIZE)
    if (idx >= BUCKET_COUNT) idx = BUCKET_COUNT - 1
    if (idx < 0) idx = 0
    buckets[idx]++
  }
  return buckets
}

function median(values: number[]): number | null {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid]
}

export function ConfidenceMasterPanel(props: MissionComponentProps) {
  const slideIndex = props.slideIndex ?? 0
  const snap = useLabComponentState('lab.assess.confidence', slideIndex)
  const responses = (snap?.aggregate.responses as Response[]) ?? []
  const total = snap?.totalPlayers ?? 0
  const values = responses
    .map((r) => Number(r.value))
    .filter((v) => Number.isFinite(v))
  const buckets = bucketize(values)
  const maxBucket = Math.max(1, ...buckets)
  const mean = values.length > 0
    ? Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10
    : null
  const med = median(values)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <header>
        <h3 style={{ margin: 0, fontSize: '1rem' }}>Distribuição de confiança</h3>
        <p style={{ margin: '4px 0 0', opacity: 0.7, fontSize: '0.85em' }}>
          {values.length} / {total} responderam
          {mean !== null && <> · média {mean} · mediana {med}</>}
        </p>
      </header>

      {values.length === 0 && (
        <div style={{ opacity: 0.6, fontStyle: 'italic', fontSize: '0.9em' }}>
          Aguardando respostas…
        </div>
      )}

      <svg viewBox="0 0 320 180" width="100%" style={{ display: 'block' }}>
        {/* eixo X com labels 0/50/100 */}
        <line x1={20} y1={150} x2={310} y2={150} stroke="currentColor" strokeOpacity={0.3} />
        {buckets.map((c, i) => {
          const x = 20 + (i * (290 / BUCKET_COUNT))
          const w = (290 / BUCKET_COUNT) - 2
          const h = (c / maxBucket) * 120
          const y = 150 - h
          return (
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={w}
                height={h}
                fill="var(--color-lab-accent, #5B2DB8)"
                opacity={0.85}
              />
              {c > 0 && (
                <text
                  x={x + w / 2}
                  y={y - 4}
                  textAnchor="middle"
                  fontSize="9"
                  fill="currentColor"
                  opacity={0.7}
                >
                  {c}
                </text>
              )}
            </g>
          )
        })}
        <text x={20} y={170} fontSize="10" fill="currentColor" opacity={0.6}>0</text>
        <text x={165} y={170} fontSize="10" fill="currentColor" opacity={0.6} textAnchor="middle">50</text>
        <text x={310} y={170} fontSize="10" fill="currentColor" opacity={0.6} textAnchor="end">100</text>
      </svg>
    </div>
  )
}
